import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import styles from './SalesPage.module.css';
import { PageShell, type PageShellRender } from '@/components/PageShell';
import { KpiStrip, type Kpi } from '@/components/KpiStrip';
import { Panel, SegmentedControl, EmptyState } from '@/components/Panel';
import { MetricTable, TrendCell, type MetricColumn } from '@/components/MetricTable';
import { LineChart } from '@/components/charts/LineChart';
import { DataSourceBanner } from '@/features/availability/DataSourceBanner';
import { fetchSales, type Cell, type Mover, type SalesRow, type SalesOverview } from '@/lib/pages.api';
import { deltaPct, deltaPoints, inr, inrExact, pctOf, plain, ratioX, shortDate, tone, units } from '@/lib/money';
import { titleCase } from '@/lib/format';

/**
 * Sales Dashboard — Overview > Sales Dashboard.
 *
 * The page answers, in this order: what did we sell, how is it trending, where
 * did it come from, what moved, and what did advertising cost against it.
 *
 * THE BASIS BANNER IS NOT DECORATION. Two sales numbers exist — the partner's
 * own portal download and the crawl-derived estimate — and they are different
 * measurements of the same rupees. The API picks one for the whole page and
 * says which; the page prints that where the reader cannot miss it, because a
 * ₹65 lakh week means something different depending on the answer.
 */

const GRAIN_LABEL = { city: 'City', category: 'Category', sku: 'SKU' } as const;
type Grain = keyof typeof GRAIN_LABEL;

const cellSub = (c: Cell, invert = false): { text: string; tone?: 'up' | 'down' } | undefined => {
  const d = deltaPct(c.delta_pct);
  if (!d) return undefined;
  const t = tone(c.delta_pct, invert);
  return t ? { text: d, tone: t } : { text: d };
};

/** For the columns that ARE percentages — they move in points, not in percent. */
const pointSub = (c: Cell, invert = false): { text: string; tone?: 'up' | 'down' } | undefined => {
  const d = deltaPoints(c.delta);
  if (!d) return undefined;
  const t = tone(c.delta, invert);
  return t ? { text: d, tone: t } : { text: d };
};

export function SalesPage({
  onNavigate,
  onSignOut,
}: {
  onNavigate?: (section: string, page: string) => void;
  onSignOut?: () => void;
}) {
  return (
    <PageShellWithData onNavigate={onNavigate} onSignOut={onSignOut} />
  );
}

function PageShellWithData({
  onNavigate,
  onSignOut,
}: {
  onNavigate?: (section: string, page: string) => void;
  onSignOut?: () => void;
}) {
  const [resolved, setResolved] = useState<SalesOverview['window'] | undefined>(undefined);

  return (
    <PageShell
      title="Sales Dashboard"
      activeNavId="sales-dashboard"
      activeSection="overview"
      {...(onNavigate ? { onNavigate } : {})}
      {...(onSignOut ? { onSignOut } : {})}
      resolved={resolved}
    >
      {(ctx) => <SalesBody ctx={ctx} onResolved={setResolved} />}
    </PageShell>
  );
}

function SalesBody({
  ctx,
  onResolved,
}: {
  ctx: PageShellRender;
  onResolved: (w: SalesOverview['window']) => void;
}) {
  const [grain, setGrain] = useState<Grain>('city');
  const [chartMetric, setChartMetric] = useState<'sales' | 'units' | 'ads'>('sales');

  const params = useMemo(
    () => ({
      customer_name: ctx.customer,
      window: ctx.range,
      filters: ctx.scope,
      limit: 10,
    }),
    [ctx.customer, ctx.range, ctx.scope],
  );

  const query = useQuery({
    queryKey: ['sales-overview', params],
    queryFn: ({ signal }) => fetchSales(params, signal),
    enabled: ctx.customer !== '',
    staleTime: 5 * 60 * 1000,
    placeholderData: (prev) => prev,
  });

  const data = query.data;
  // The top bar shows the window the API actually resolved — anchored to the
  // feed's latest day, not to the clock — so it is echoed back up to the shell
  // once the response lands.
  const win = data?.window;
  useEffect(() => {
    if (win) onResolved(win);
  }, [win, onResolved]);

  const loading = query.isPending && !data;
  const basisActual = data?.basis === 'actual';

  const kpis: Kpi[] = useMemo(() => {
    const k = data?.kpis;
    const money = (c: Cell | undefined): string => inr(c?.value ?? null);
    return [
      {
        key: 'sales',
        label: basisActual ? 'Net sales (actual)' : 'Net sales (estimated)',
        value: money(k?.sales),
        delta: deltaPct(k?.sales.delta_pct),
        tone: tone(k?.sales.delta_pct),
        foot: k?.sales.previous != null ? `was ${inr(k.sales.previous)}` : undefined,
        help: data?.response_meta.basis_note,
        emphasis: true,
      },
      {
        key: 'units',
        label: 'Units',
        value: units(k?.units.value ?? null),
        delta: deltaPct(k?.units.delta_pct),
        tone: tone(k?.units.delta_pct),
      },
      {
        key: 'units_per_day',
        label: 'Units / day',
        value: plain(k?.units_per_day.value ?? null),
        delta: deltaPct(k?.units_per_day.delta_pct),
        tone: tone(k?.units_per_day.delta_pct),
        help: 'Units divided by the days that actually carry data, not by the days in the window — a SKU that launched mid-week is not reported as selling a fraction of what it sells.',
      },
      {
        key: 'asp',
        label: 'Avg selling price',
        value: inrExact(k?.asp.value ?? null),
        delta: deltaPct(k?.asp.delta_pct),
        tone: tone(k?.asp.delta_pct),
      },
      {
        key: 'discount_pct',
        label: 'Discount',
        value: pctOf(k?.discount_pct.value ?? null),
        delta: deltaPoints(k?.discount_pct.delta),
        // Deeper discounting is not an improvement.
        tone: tone(k?.discount_pct.delta, true),
        help: 'Always the crawl pair — both prices observed on the same shelf on the same day — so this figure matches the Discount page whichever sales basis the rest of this page reads.',
      },
      {
        key: 'wt_osa',
        label: 'Weighted OSA',
        value: pctOf(k?.wt_osa.value ?? null),
        delta: deltaPoints(k?.wt_osa.delta),
        tone: tone(k?.wt_osa.delta),
        help: 'On-shelf availability weighted by what actually sells, so a store that moves two hundred units a week counts for more than one that moves two.',
      },
      {
        key: 'ad_spend',
        label: 'Ad spend',
        value: money(k?.ad_spend),
        delta: deltaPct(k?.ad_spend.delta_pct),
        tone: tone(k?.ad_spend.delta_pct, true),
      },
      {
        key: 'roas',
        label: 'ROAS',
        value: ratioX(k?.roas.value ?? null),
        delta: deltaPct(k?.roas.delta_pct),
        tone: tone(k?.roas.delta_pct),
        foot: k?.acos.value != null ? `ACOS ${pctOf(k.acos.value)}` : undefined,
      },
      {
        key: 'tacos',
        label: 'Ad spend / total sales',
        value: pctOf(k?.tacos.value ?? null),
        delta: deltaPoints(k?.tacos.delta, 2),
        tone: tone(k?.tacos.delta, true),
        help: 'Ad spend against ALL sales, not just attributed sales — the share of the business advertising is paying for.',
      },
    ];
  }, [data, basisActual]);

  const chart = useMemo(() => {
    const s = data?.series ?? [];
    const labels = s.map((p) => shortDate(p.day));
    if (chartMetric === 'ads') {
      return {
        labels,
        series: [
          {
            key: 'spend',
            label: 'Ad spend',
            color: '#d03941',
            points: s.map((p) => p.ad_spend),
            format: (v: number) => inr(v),
          },
          {
            key: 'ad_sales',
            label: 'Attributed sales',
            color: '#342885',
            points: s.map((p) => p.ad_sales),
            format: (v: number) => inr(v),
          },
        ],
      };
    }
    if (chartMetric === 'units') {
      return {
        labels,
        series: [
          {
            key: 'units',
            label: basisActual ? 'Units (actual)' : 'Units (estimated)',
            color: '#342885',
            points: s.map((p) => (basisActual ? p.units_actual : p.units_est)),
            format: (v: number) => units(v),
          },
        ],
      };
    }
    /**
     * Both sales lines are drawn, always — the actual one solid-coloured and the
     * estimate beside it. They are two measurements of the same rupees and the
     * gap between them is information: it is the estimate's error, visible.
     * Drawing only the basis would hide the fact that the actual line simply
     * STOPS a day or two before the window ends, which otherwise reads as a
     * collapse in sales.
     */
    return {
      labels,
      series: [
        {
          key: 'actual',
          label: 'Sales (partner actuals)',
          color: '#342885',
          points: s.map((p) => p.sales_actual),
          format: (v: number) => inr(v),
        },
        {
          key: 'est',
          label: 'Sales (crawl estimate)',
          color: '#b7b7b7',
          points: s.map((p) => p.sales_est),
          format: (v: number) => inr(v),
        },
      ],
    };
  }, [data, chartMetric, basisActual]);

  const tableRows = data?.tables[grain].rows ?? [];

  const columns: MetricColumn<SalesRow>[] = useMemo(() => {
    const base: MetricColumn<SalesRow>[] = [
      {
        key: 'label',
        header: GRAIN_LABEL[grain],
        align: 'left',
        width: '26%',
        render: (r) => (grain === 'city' ? (titleCase(r.label) ?? r.label) : r.label),
        sub: (r) =>
          r.sku_count != null ? { text: `${plain(r.sku_count)} SKUs` } : undefined,
      },
      {
        key: 'sales',
        header: basisActual ? 'Sales (actual)' : 'Sales (est.)',
        render: (r) => inr(r.sales.value),
        sub: (r) => cellSub(r.sales),
      },
      {
        key: 'share',
        header: 'Share',
        render: (r) => pctOf(r.share_pct),
      },
      {
        key: 'trend',
        header: 'Trend',
        align: 'center',
        render: (r) => <TrendCell points={r.series} />,
      },
      { key: 'units', header: 'Units', render: (r) => units(r.units.value), sub: (r) => cellSub(r.units) },
      { key: 'asp', header: 'ASP', render: (r) => inrExact(r.asp.value), sub: (r) => cellSub(r.asp) },
      {
        key: 'discount',
        header: 'Discount',
        render: (r) => pctOf(r.discount_pct.value),
        sub: (r) => pointSub(r.discount_pct, true),
      },
      {
        key: 'osa',
        header: 'Wt OSA',
        render: (r) => pctOf(r.wt_osa.value),
        sub: (r) => pointSub(r.wt_osa),
      },
    ];
    if (grain === 'sku') {
      base.push({
        key: 'ads',
        header: 'Ad spend',
        help: 'ASSOCIATED spend: the whole spend of every campaign targeting this SKU. Correct on this row and meaningless added to the next one, so it is never totalled.',
        render: (r) => inr(r.ads?.associated_spend ?? null),
        sub: (r) =>
          r.ads?.associated_roas != null
            ? { text: `${ratioX(r.ads.associated_roas)} ROAS` }
            : undefined,
      });
    }
    return base;
  }, [grain, basisActual]);

  const moverColumns: MetricColumn<Mover>[] = [
    {
      key: 'label',
      header: 'What moved',
      align: 'left',
      width: '46%',
      render: (m) => (m.grain === 'city' ? (titleCase(m.label) ?? m.label) : m.label),
      sub: (m) => ({ text: GRAIN_LABEL[m.grain] }),
    },
    { key: 'now', header: 'This period', render: (m) => inr(m.sales) },
    { key: 'prev', header: 'Previous', render: (m) => inr(m.previous) },
    {
      key: 'delta',
      header: 'Change',
      render: (m) => inr(m.delta, { sign: true }),
      sub: (m) => {
        const d = deltaPct(m.delta_pct);
        const t = tone(m.delta_pct);
        return d ? (t ? { text: d, tone: t } : { text: d }) : undefined;
      },
    },
  ];

  return (
    <>
      {query.isError && (
        <DataSourceBanner kind="error" detail={query.error.message} />
      )}

      {data && (
        <div
          className={`${styles.basis} ${basisActual ? styles.basisActual : styles.basisEstimate}`}
        >
          <strong>{basisActual ? 'Partner actuals' : 'Crawl estimate'}</strong>
          <span>{data.response_meta.basis_note}</span>
          {data.actuals_through && !basisActual ? null : null}
        </div>
      )}

      <KpiStrip items={kpis} loading={loading} />

      <Panel
        title="Daily trend"
        caption={
          data
            ? `${shortDate(data.window.start)} – ${shortDate(data.window.end)} · previous period ${shortDate(data.window.previous_start)} – ${shortDate(data.window.previous_end)}`
            : undefined
        }
        actions={
          <SegmentedControl
            label="Trend metric"
            value={chartMetric}
            onChange={setChartMetric}
            options={[
              { value: 'sales', label: 'Sales' },
              { value: 'units', label: 'Units' },
              { value: 'ads', label: 'Advertising' },
            ]}
          />
        }
        footnote={
          chartMetric === 'sales'
            ? 'Both measurements are drawn. The partner line stops where the portal feed stops — it is not a drop in sales, and it is not filled in with the estimate.'
            : undefined
        }
      >
        {chart.labels.length === 0 ? (
          <EmptyState>No daily figures for this window.</EmptyState>
        ) : (
          <LineChart labels={chart.labels} series={chart.series} height={260} />
        )}
      </Panel>

      <Panel
        title="Where the sales are"
        caption="Every ratio is re-derived from summed atoms; the share column is of the whole filtered scope, not of the rows shown."
        footnote={
          basisActual
            ? 'A dash in the sales columns means the partner feed carries no figure for that row — the crawl still sees it, which is why it has an availability number. It is not a zero.'
            : undefined
        }
        actions={
          <SegmentedControl
            label="Breakdown grain"
            value={grain}
            onChange={setGrain}
            options={[
              { value: 'city', label: 'City' },
              { value: 'category', label: 'Category' },
              { value: 'sku', label: 'SKU' },
            ]}
          />
        }
        dense
      >
        <MetricTable
          searchable
          caption={`Sales by ${GRAIN_LABEL[grain]}`}
          columns={columns}
          rows={tableRows}
          rowKey={(r) => r.id}
          loading={loading}
          onRowClick={(r) => {
            const value = r.value ?? r.label;
            ctx.setScope({ ...ctx.scope, [grain === 'sku' ? 'sku' : grain]: value });
          }}
          empty={`No ${GRAIN_LABEL[grain].toLowerCase()} rows for this scope.`}
        />
      </Panel>

      <div className={styles.split}>
        <Panel title="Gained the most" dense>
          <MetricTable
          searchable
            caption="Biggest gainers"
            columns={moverColumns}
            rows={data?.movers.gainers ?? []}
            rowKey={(m) => m.id}
            loading={loading}
            skeletonRows={5}
            empty="Nothing grew against the previous period."
          />
        </Panel>
        <Panel title="Lost the most" dense>
          <MetricTable
          searchable
            caption="Biggest decliners"
            columns={moverColumns}
            rows={data?.movers.decliners ?? []}
            rowKey={(m) => m.id}
            loading={loading}
            skeletonRows={5}
            empty="Nothing declined against the previous period."
          />
        </Panel>
      </div>

      <Panel
        title="Advertising"
        caption="Campaign spend and attributed sales. Additive and safe to total — unlike the per-SKU association in the table above."
        footnote={
          data && !data.response_meta.ads_available
            ? 'No advertising data for this brand in this window.'
            : undefined
        }
        dense
      >
        <MetricTable
          searchable
          caption="Campaigns by spend"
          columns={[
            {
              key: 'name',
              header: 'Campaign',
              align: 'left',
              width: '38%',
              render: (c) => c.campaign_name,
              sub: (c) => (c.campaign_type ? { text: c.campaign_type } : undefined),
            },
            { key: 'spend', header: 'Spend', render: (c) => inr(c.spend) },
            { key: 'share', header: 'Spend share', render: (c) => pctOf(c.spend_share_pct) },
            { key: 'sales', header: 'Attributed sales', render: (c) => inr(c.ad_sales) },
            { key: 'roas', header: 'ROAS', render: (c) => ratioX(c.roas) },
            { key: 'acos', header: 'ACOS', render: (c) => pctOf(c.acos) },
            { key: 'units', header: 'Units', render: (c) => units(c.units) },
          ]}
          rows={data?.campaigns ?? []}
          rowKey={(c) => String(c.campaign_id)}
          loading={loading}
          empty="No campaigns ran in this window."
        />
      </Panel>
    </>
  );
}
